<template>
    <div class="flex">
        <div class="hidden lg:block grow" v-if="!isLoading">
            <div class="sticky top-48">
                <div class="border-y-2 border-red-dark py-8">
                    <ul class="grid gap-8">
                        <li class="grid gap-4" v-for="yearGroup in options" :key="yearGroup.year">
                            <span class="heading-md" :class="yearGroup.year === currentYear ? 'text-red-dark' : 'text-black'">{{
                                yearGroup.year
                            }}</span>
                            <ul class="grid gap-4 text-lg leading-none">
                                <li v-for="item in yearGroup.items" :key="item.value">
                                    <a
                                        :href="`#month-${item.value}`"
                                        class="inline-flex gap-2 items-center"
                                        :class="{
                                            'text-red-dark': item.value === currentMonth,
                                            'pointer-events-none opacity-30': !item.count,
                                        }"
                                    >
                                        <span class="heading-xl">{{ item.label }}</span>
                                        <span
                                            v-if="item.count"
                                            class="-mt-2 size-6 text-sm leading-none flex items-center justify-center rounded-xl font-sans bg-sand text-black font-bold"
                                            >{{ item.count }}</span
                                        >
                                    </a>
                                </li>
                            </ul>
                        </li>
                    </ul>
                </div>
                <div class="flex mt-8 justify-end">
                    <a
                        href="#app"
                        class="button text-red-dark hover:inset-ring-red-dark! hover:text-white! hover:bg-red-dark items-center gap-2 rounded-full"
                        ><span>Back to Top</span></a
                    >
                </div>
            </div>
        </div>
        <!-- Mobile drop down -->
        <Menu as="div" class="sticky top-64 w-full lg:hidden z-30">
            <MenuButton class="inline-flex w-full justify-center gap-x-2 rounded-md bg-sand-light p-4 font-bold text-black">
                Skip to month
                <ChevronDownIcon class="-mr-1 size-6 text-black" aria-hidden="true" />
            </MenuButton>

            <transition
                enter-active-class="transition ease-out duration-100"
                enter-from-class="transform opacity-0 scale-95"
                enter-to-class="transform scale-100"
                leave-active-class="transition ease-in duration-75"
                leave-from-class="transform scale-100"
                leave-to-class="transform opacity-0 scale-95"
            >
                <MenuItems class="absolute left-0 w-full z-20 mt-2 origin-top-left max-h-80 overflow-y-auto rounded-md bg-ivory shadow-lg p-6">
                    <div v-for="yearGroup in options" :key="yearGroup.year">
                        <span class="heading-md" :class="yearGroup.year === currentYear ? 'text-red-dark' : 'text-black'">{{ yearGroup.year }}</span>
                        <ul class="grid gap-2 text-lg leading-none p-4">
                            <MenuItem v-slot="{ active }" v-for="item in yearGroup.items" :key="item.value">
                                <li>
                                    <a
                                        :href="`#month-${item.value}`"
                                        class="inline-flex gap-2 items-center"
                                        :class="{
                                            'text-red-dark': item.value === currentMonth,
                                            'pointer-events-none opacity-30': !item.count,
                                        }"
                                    >
                                        <span class="heading-xl">{{ item.label }}</span>
                                        <span
                                            v-if="item.count"
                                            class="-mt-2 size-6 text-sm leading-none flex items-center justify-center rounded-xl font-sans bg-sand text-black font-bold"
                                            >{{ item.count }}</span
                                        >
                                    </a>
                                </li>
                            </MenuItem>
                        </ul>
                    </div>
                </MenuItems>
            </transition>
        </Menu>
        <a
            href="#app"
            class="button md:hidden text-red-dark hover:inset-ring-red-dark! hover:text-white! hover:bg-red-dark bg-ivory items-center gap-2 rounded-full fixed bottom-6 right-4 z-40"
            ><span>Back to Top</span></a
        >
    </div>
</template>

<script setup>
import { computed } from "vue";
import { parse, format } from "date-fns";
import { storeToRefs } from "pinia";
import { useFeedStore } from "@facet/utils/store.js";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/vue";
import { ChevronDownIcon } from "@heroicons/vue/20/solid";

const store = useFeedStore();
const { currentMonth, isLoading } = storeToRefs(store);

const props = defineProps({
    modelValue: {
        type: String, // format: "YYYY-MM"
        required: true,
    },
    options: {
        type: Array,
        required: true,
    },
});

const emit = defineEmits(["update:modelValue"]);

const currentDate = computed(() => parse(props.modelValue, "yyyy-MM", new Date()));
const currentYear = computed(() => Number(format(currentDate.value, "yyyy")));
</script>
